/**
 * Agregações de SEO a partir das páginas rastreadas.
 *
 * Função pura, separada da tela de propósito: os números que orientam decisão
 * do cliente merecem ser testáveis sem montar componente. E foi um erro de
 * aritmética silencioso — soma de string vinda de BIGINT — que produziu o
 * primeiro relatório de IA com dados impossíveis.
 *
 * Os limiares abaixo seguem a orientação pública do Google para Busca. Onde a
 * documentação não fixa número, o comentário registra de onde veio o valor.
 */

/**
 * O driver do Postgres devolve BIGINT como string (`page_size_bytes`), e
 * colunas ausentes chegam nulas. Converter antes de qualquer conta evita que
 * `soma + valor` concatene em vez de somar.
 */
export const num = (valor) => {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
};

/** `internal_links` é TEXT[] no banco, mas pode chegar como número já contado. */
const contar = (valor) => {
  if (Array.isArray(valor)) return valor.length;
  return num(valor);
};

const pct = (parte, total) => (total > 0 ? Math.round((parte / total) * 100) : 0);

/** Divide as páginas em grupos mutuamente exclusivos, sem laços repetidos. */
const agrupar = (paginas, classificar) =>
  paginas.reduce((acc, pagina) => {
    const chave = classificar(pagina);
    acc[chave] = (acc[chave] || 0) + 1;
    return acc;
  }, {});

// ---------------------------------------------------------------------------
// Limiares
// ---------------------------------------------------------------------------

export const LIMIARES = {
  // O Google não publica tamanho ideal de título; usa o que couber no snippet.
  // 30–60 caracteres é a faixa em que o título raramente é truncado na SERP.
  TITULO_MIN: 30,
  TITULO_MAX: 60,

  // Mesma lógica para a meta description: 120–160 é a faixa exibida sem corte
  // na maioria dos resultados.
  META_MIN: 120,
  META_MAX: 160,

  // Core Web Vitals: LCP "bom" até 2,5s. Não medimos LCP, e sim o tempo de
  // resposta do servidor — por isso o alerta é mais folgado, em 3s.
  CARGA_LENTA_MS: 3000,
  CARGA_CRITICA_MS: 5000,

  // Página muito pesada compromete carregamento em rede móvel. 2 MB é o ponto
  // a partir do qual isso costuma doer de forma perceptível.
  PAGINA_PESADA_BYTES: 2 * 1024 * 1024,

  // "Conteúdo raso" não tem número oficial — o Google avalia utilidade, não
  // contagem. 300 palavras é usado aqui apenas como sinalizador de páginas que
  // merecem revisão humana, não como regra.
  CONTEUDO_RASO_PALAVRAS: 300
};

// ---------------------------------------------------------------------------
// Seções
// ---------------------------------------------------------------------------

/**
 * Página que respondeu mas não entregou conteúdo utilizável.
 *
 * O crawler decide isso na coleta, depois de esgotar as retentativas, e grava
 * em `render_failed`. Quando a coluna traz booleano, ela manda — só ele sabe
 * quantas tentativas foram feitas.
 *
 * A inferência serve às páginas coletadas antes da coluna existir. Ela erra num
 * caso: uma falha que teria se resolvido numa segunda tentativa aparece como
 * permanente. É o melhor possível sem ter tentado de novo na época.
 */
const naoRenderizou = (p) => {
  if (typeof p.render_failed === 'boolean') return p.render_failed;

  const codigo = num(p.status_code);
  return (
    codigo >= 200 &&
    codigo < 300 &&
    !(p.title || '').trim() &&
    contar(p.h1_tags) === 0 &&
    contar(p.internal_links) === 0
  );
};

export const metricasIndexacao = (paginas) => {
  const total = paginas.length;

  const porFaixa = agrupar(paginas, (p) => {
    const codigo = num(p.status_code);
    if (codigo >= 500) return '5xx';
    if (codigo >= 400) return '4xx';
    if (codigo >= 300) return '3xx';
    if (codigo >= 200) return '2xx';
    return 'outro';
  });

  const comErro = (porFaixa['4xx'] || 0) + (porFaixa['5xx'] || 0);
  const semCanonical = paginas.filter((p) => !p.has_canonical_tag).length;
  const comRobots = paginas.filter((p) => p.has_robots_meta).length;
  const semRenderizacao = paginas.filter(naoRenderizou).length;

  return {
    total,
    porFaixa,
    comErro,
    percentualAcessivel: pct(porFaixa['2xx'] || 0, total),
    semCanonical,
    comRobots,
    semRenderizacao,
    distribuicao: Object.entries(porFaixa).map(([faixa, quantidade]) => ({
      name: faixa,
      value: quantidade
    }))
  };
};

export const metricasConteudo = (paginas) => {
  const total = paginas.length;

  const semTitulo = paginas.filter((p) => !p.title || p.title.trim() === '').length;
  const semDescricao = paginas.filter(
    (p) => !p.meta_description || p.meta_description.trim() === ''
  ).length;
  const semH1 = paginas.filter((p) => contar(p.h1_tags) === 0).length;

  const tituloForaDaFaixa = paginas.filter((p) => {
    const tamanho = num(p.title_length) || (p.title || '').length;
    return tamanho > 0 && (tamanho < LIMIARES.TITULO_MIN || tamanho > LIMIARES.TITULO_MAX);
  }).length;

  const descricaoForaDaFaixa = paginas.filter((p) => {
    const tamanho = num(p.meta_description_length) || (p.meta_description || '').length;
    return tamanho > 0 && (tamanho < LIMIARES.META_MIN || tamanho > LIMIARES.META_MAX);
  }).length;

  const conteudoRaso = paginas.filter(
    (p) => num(p.word_count) > 0 && num(p.word_count) < LIMIARES.CONTEUDO_RASO_PALAVRAS
  ).length;

  return {
    total,
    semTitulo,
    semDescricao,
    semH1,
    tituloForaDaFaixa,
    descricaoForaDaFaixa,
    conteudoRaso,
    // Barras para comparar o que está completo contra o que falta.
    cobertura: [
      { name: 'metrica.titulo', ok: total - semTitulo, falta: semTitulo },
      { name: 'metrica.descricao', ok: total - semDescricao, falta: semDescricao },
      { name: 'metrica.h1', ok: total - semH1, falta: semH1 }
    ]
  };
};

export const metricasExperiencia = (paginas) => {
  const total = paginas.length;

  const somaCarga = paginas.reduce((s, p) => s + num(p.load_time_ms), 0);
  const somaTamanho = paginas.reduce((s, p) => s + num(p.page_size_bytes), 0);

  const lentas = paginas.filter((p) => num(p.load_time_ms) >= LIMIARES.CARGA_LENTA_MS).length;
  const criticas = paginas.filter((p) => num(p.load_time_ms) >= LIMIARES.CARGA_CRITICA_MS).length;
  const pesadas = paginas.filter(
    (p) => num(p.page_size_bytes) >= LIMIARES.PAGINA_PESADA_BYTES
  ).length;
  const semViewport = paginas.filter((p) => !p.has_viewport_meta).length;

  const faixas = agrupar(paginas, (p) => {
    const ms = num(p.load_time_ms);
    if (ms < 1000) return '0–1s';
    if (ms < 2000) return '1–2s';
    if (ms < 3000) return '2–3s';
    return '3s+';
  });

  return {
    total,
    cargaMedia: total > 0 ? Math.round(somaCarga / total) : 0,
    tamanhoMedioKb: total > 0 ? Math.round(somaTamanho / total / 1024) : 0,
    lentas,
    criticas,
    pesadas,
    semViewport,
    // Ordem fixa: `agrupar` não garante ordenação e o gráfico ficaria embaralhado.
    distribuicaoCarga: ['0–1s', '1–2s', '2–3s', '3s+'].map((faixa) => ({
      name: faixa,
      value: faixas[faixa] || 0
    }))
  };
};

export const metricasMidia = (paginas) => {
  const comAlt = paginas.reduce((s, p) => s + num(p.images_with_alt_count), 0);
  const semAlt = paginas.reduce((s, p) => s + num(p.images_without_alt_count), 0);
  const totalImagens = comAlt + semAlt;

  return {
    totalImagens,
    comAlt,
    semAlt,
    percentualComAlt: pct(comAlt, totalImagens),
    paginasComImagemSemAlt: paginas.filter((p) => num(p.images_without_alt_count) > 0).length,
    distribuicao: [
      { name: 'metrica.comAlt', value: comAlt },
      { name: 'metrica.semAlt', value: semAlt }
    ]
  };
};

export const metricasLinks = (paginas) => {
  const total = paginas.length;

  const internos = paginas.reduce((s, p) => s + contar(p.internal_links), 0);
  const externos = paginas.reduce((s, p) => s + contar(p.external_links), 0);
  const quebrados = paginas.reduce((s, p) => s + num(p.broken_links_count), 0);

  return {
    total,
    internos,
    externos,
    quebrados,
    mediaInternos: total > 0 ? Math.round(internos / total) : 0,
    // Página sem link interno de saída é beco sem saída para o rastreador.
    semLinkInterno: paginas.filter((p) => contar(p.internal_links) === 0).length,
    paginasComQuebrados: paginas.filter((p) => num(p.broken_links_count) > 0).length,
    distribuicao: [
      { name: 'metrica.interno', value: internos },
      { name: 'metrica.externo', value: externos }
    ]
  };
};

/**
 * Contagem de problemas por seção, usada nas abas.
 *
 * Mostrar o número na aba é o que faz o usuário saber onde olhar sem abrir
 * cada uma — o dashboard antigo obrigava a percorrer tudo.
 */
export const problemasPorSecao = (paginas) => {
  const indexacao = metricasIndexacao(paginas);
  const conteudo = metricasConteudo(paginas);
  const experiencia = metricasExperiencia(paginas);
  const midia = metricasMidia(paginas);
  const links = metricasLinks(paginas);

  return {
    // Falha de render entra aqui, não em conteúdo: a página não chega a ser
    // lida, então o problema é de acesso, não do que está escrito nela.
    indexacao: indexacao.comErro + indexacao.semCanonical + indexacao.semRenderizacao,
    conteudo: conteudo.semTitulo + conteudo.semDescricao + conteudo.semH1,
    experiencia: experiencia.lentas + experiencia.semViewport,
    midia: midia.paginasComImagemSemAlt,
    links: links.paginasComQuebrados + links.semLinkInterno
  };
};

/**
 * Quantas páginas em cada nível de clique, agrupando 4 ou mais no fim.
 *
 * `depth` é `Option<i16>` no crawler: uma execução repetida de antes da coluna
 * existir não tem nível de onde herdar, e o valor chega nulo. Nulo não vira
 * zero aqui — zero é "página inicial", uma medição, e não a falta dela. Sem
 * nenhuma página com nível, `medidas` é zero e a tela esconde o gráfico em vez
 * de desenhar cinco barras vazias.
 */
export const distribuicaoDeProfundidade = (paginas = []) => {
  const faixas = [0, 0, 0, 0, 0];
  let medidas = 0;

  paginas.forEach((p) => {
    if (p.depth === null || p.depth === undefined || p.depth === '') return;
    const nivel = Number(p.depth);
    if (!Number.isFinite(nivel) || nivel < 0) return;
    faixas[Math.min(nivel, 4)] += 1;
    medidas += 1;
  });

  return { medidas, faixas };
};
